import { el } from '../utils/dom.js';

/**
 * Resize the current map — change dimensions while preserving existing tile data.
 */
export class ResizeMapDialog {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
  }

  show() {
    const currentW = this.state.get('mapWidth');
    const currentH = this.state.get('mapHeight');

    const overlay = el('div', { className: 'dialog-overlay' });
    const dialog = el('div', { className: 'dialog' });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Resize Map'));
    dialog.appendChild(el('p', { style: { fontSize: '12px', color: 'var(--text-dim)', marginBottom: '12px' } },
      `Current size: ${currentW} x ${currentH} tiles`));

    const row = el('div', { className: 'form-row' });

    const wField = el('div', { className: 'form-field' });
    wField.append(el('label', { className: 'form-label-sm' }, 'New Width'));
    const wInput = el('input', { type: 'number', className: 'form-input', value: String(currentW), min: '5', max: '500' });
    wField.appendChild(wInput);
    row.appendChild(wField);

    const hField = el('div', { className: 'form-field' });
    hField.append(el('label', { className: 'form-label-sm' }, 'New Height'));
    const hInput = el('input', { type: 'number', className: 'form-input', value: String(currentH), min: '5', max: '500' });
    hField.appendChild(hInput);
    row.appendChild(hField);
    dialog.appendChild(row);

    // Anchor selector
    const anchorGroup = el('div', { className: 'form-group' });
    anchorGroup.appendChild(el('label', { className: 'form-label' }, 'Anchor'));
    const anchorGrid = el('div', { className: 'anchor-grid' });
    const anchors = ['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right'];
    let selectedAnchor = 'top-left';

    for (const anchor of anchors) {
      const btn = el('button', {
        className: `anchor-btn${anchor === selectedAnchor ? ' active' : ''}`,
        title: anchor,
        onClick: () => {
          anchorGrid.querySelectorAll('.anchor-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          selectedAnchor = anchor;
        },
      });
      anchorGrid.appendChild(btn);
    }
    anchorGroup.appendChild(anchorGrid);
    dialog.appendChild(anchorGroup);

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const resizeBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      const newW = parseInt(wInput.value, 10) || currentW;
      const newH = parseInt(hInput.value, 10) || currentH;
      this._resize(currentW, currentH, newW, newH, selectedAnchor);
      overlay.remove();
    }}, 'Resize');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(resizeBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  _resize(oldW, oldH, newW, newH, anchor) {
    // Calculate offset based on anchor
    let offsetX = 0, offsetY = 0;
    if (anchor.includes('right')) offsetX = newW - oldW;
    else if (anchor === 'top' || anchor === 'center' || anchor === 'bottom') offsetX = Math.floor((newW - oldW) / 2);

    if (anchor.includes('bottom')) offsetY = newH - oldH;
    else if (anchor === 'left' || anchor === 'center' || anchor === 'right') offsetY = Math.floor((newH - oldH) / 2);

    const layers = this.state.get('layers');

    for (const layer of layers) {
      if (layer.type !== 'tilelayer' || !layer.data) continue;

      const newData = new Uint32Array(newW * newH);

      for (let y = 0; y < oldH; y++) {
        for (let x = 0; x < oldW; x++) {
          const nx = x + offsetX;
          const ny = y + offsetY;
          if (nx >= 0 && nx < newW && ny >= 0 && ny < newH) {
            newData[ny * newW + nx] = layer.data[y * oldW + x];
          }
        }
      }

      layer.data = newData;
    }

    this.state.batch({
      mapWidth: newW,
      mapHeight: newH,
      dirty: true,
    });

    this.mapCanvas.clear();
    this.mapCanvas.invalidateAll();
    this.compositor.requestRender();
  }
}
